import { Box, Flex, Text, Heading } from '@everlywell/ui-kit';
import React from 'react';

import BenefitCardDescriptionBullet, {
  BenefitCardDescriptionBulletProps,
} from './BenefitCardDescriptionBullet';

/**
 * Type definition for a part of the benefit card title
 * @typedef {Object} BenefitCardTitlePart
 * @property {string} [text] The text content of the title part
 * @property {string} [color] The color of the title part text
 * @property {string} [highlightColor] Color applied to the highlighted text segment
 * @property {string} [highlightText] Text segment that should be highlighted within the title part
 * @property {string} [endChar] Character to append at the end of the text (defaults to comma)
 */
export type BenefitCardTitlePart = {
  text?: string | null;
  color?: string | null;
  highlightColor?: string | null;
  highlightText?: string | null;
  endChar?: string | null;
};

/**
 * Props for the BenefitCardDescription component
 * @typedef {Object} BenefitCardDescriptionProps
 * @property {BenefitCardTitlePart} [topTitlePart] The top part of the title with text and color properties
 * @property {BenefitCardTitlePart} [bottomTitlePart] The bottom part of the title with text and color properties
 * @property {string | null} [backgroundColor] Background color for the card
 * @property {BenefitCardDescriptionBulletProps[]} [bullets] Array of bullet points to display
 */
export type BenefitCardDescriptionProps = {
  topTitlePart?: BenefitCardTitlePart;
  bottomTitlePart?: BenefitCardTitlePart;
  backgroundColor?: string | null;
  bullets?: BenefitCardDescriptionBulletProps[];
  slots?: BenefitCardDescriptionSlotOverrides;
};

type SlotOverride = {
  sx?: any;
};

export type BenefitCardDescriptionSlotOverrides = {
  root?: SlotOverride;
  titleContainer?: SlotOverride;
  title?: SlotOverride;
  titleTopPart?: SlotOverride;
  titleBottomPart?: SlotOverride;
  bullets?: SlotOverride;
};

// Notice: The endChar parameter is necessary because the trailing comma is based into the
// component. We must update Contentful to include the comma in the content before endChar can
// deprecated.
export const splitTextWithTrailingCommas = (text?: string, endChar?: string): string[] => {
  if (!text) {
    return [];
  }

  const char = endChar || ',';
  const normalizedText = text.endsWith(char) ? text : `${text}${char}`;

  // Match all groups ending with the specified endChar and trim whitespaces
  const escapeChar = char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`[^${escapeChar}]+${escapeChar}`, "g");
  return (normalizedText.match(regex) || []).map((text) => text.trim());
};

// Adds a highlight without having to manually add a <span> around the portion to highlight.
const renderTextWithHighlight = (
  text: string,
  highlightText?: string | null,
  highlightColor?: string | null,
): React.ReactNode => {
  if (!highlightText || !highlightColor) {
    return text;
  }

  const highlightIndex = text.indexOf(highlightText);

  if (highlightIndex === -1) {
    return text;
  }

  const before = text.slice(0, highlightIndex);
  const after = text.slice(highlightIndex + highlightText.length);

  return (
    <>
      {before}
      <Text as="span" color={highlightColor} fontSize="inherit">
        {highlightText}
      </Text>
      {after}
    </>
  );
};

/**
 * BenefitCardDescription component
 * A component that displays a card with a two-part title and a list of bullet points.
 * The title is split into top and bottom parts, with each part potentially having different colors.
 * Each title part can contain multiple comma-separated segments that are displayed on separate lines.
 * @param {BenefitCardTitlePart} [props.topTitlePart] The top part of the title with text and color properties
 * @param {BenefitCardTitlePart} [props.bottomTitlePart] The bottom part of the title with text and color properties
 * @param {string | null} [props.backgroundColor] Background color for the card
 * @param {BenefitCardDescriptionBulletProps[]} [props.bullets] Array of bullet points to display
 * @returns {JSX.Element} BenefitCardDescription component
 */
export default function BenefitCardDescription({
  topTitlePart,
  bottomTitlePart,
  backgroundColor,
  bullets,
  slots,
}: BenefitCardDescriptionProps): JSX.Element {
  const topTopTitleTextSeparatedByCommas = splitTextWithTrailingCommas(
    topTitlePart?.text ?? '',
    topTitlePart?.endChar ?? undefined,
  );
  const bottomTitleTextSeparatedByCommas = splitTextWithTrailingCommas(
    bottomTitlePart?.text ?? '',
    bottomTitlePart?.endChar ?? undefined,
  );

  return (
    <Flex
      backgroundColor={backgroundColor || ''}
      width="100%"
      padding={[6, null, 8]}
      borderRadius="2xl"
      flexDirection="column"
      justifyContent="space-between"
      data-testid="benefit-card-description"
      sx={slots?.root?.sx}
    >
      <Box
        marginBottom={[12, null, 8]}
        sx={slots?.titleContainer?.sx}
      >
        <Heading
          size={['md', null, 'lg']}
          display="flex"
          flexDirection="column"
          sx={slots?.title?.sx}
        >
          {topTopTitleTextSeparatedByCommas.map((text, index) => (
            <Text
              key={index}
              as="span"
              color={topTitlePart?.color || ''}
              fontSize="inherit"
              text-shadow=" 0px 4px 4px rgba(0, 0, 0, 0.25)"
              sx={slots?.titleTopPart?.sx}
            >
              {renderTextWithHighlight(
                text,
                topTitlePart?.highlightText,
                topTitlePart?.highlightColor,
              )}
            </Text>
          ))}
          {bottomTitleTextSeparatedByCommas.map((text, index) => (
            <Text
              key={index}
              as="span"
              color={bottomTitlePart?.color || ''}
              fontSize="inherit"
              sx={slots?.titleBottomPart?.sx}
            >
              {/* Remove trailing comma from last item */}
              {bottomTitleTextSeparatedByCommas.length - 1 === index
                ? text.replace(/,$/, '')
                : text}
            </Text>
          ))}
        </Heading>
      </Box>
      {bullets && bullets.length > 0 && (
        <Flex
          flexDirection="column"
          gap={[4, null, 6]}
          sx={slots?.bullets?.sx}
        >
          {bullets.map((bullet, index) => (
            <BenefitCardDescriptionBullet
              key={index}
              iconName={bullet.iconName}
              iconColor={bullet.iconColor}
              title={bullet.title}
              description={bullet.description}
              textColor={bullet.textColor}
              primaryColor={bullet.primaryColor}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
}
